/**
 * THE REAL IMPLEMENTATION. One file, sixteen calls, one `request` helper.
 *
 * This is the whole of the fetch side of the seam. Swapping the fixture for the network is
 * `create.ts` choosing this factory instead — not a change in any screen, hook or component.
 *
 * TRANSPORT FACTS THIS FILE HAS TO GET RIGHT, all ruled elsewhere:
 * - The cabinet is served at `authors.moonx.app`; the API base is `https://moonx.art/authors-api`
 *   and Caddy's `handle_path` strips the prefix so the process sees `/v1/...`
 *   (adr/0002-edge-addressing.md, authors-api.yaml servers).
 * - DIFFERENT ORIGINS, so every call is cross-origin and BEARER-ONLY. Firebase sessions are
 *   per-origin and the cabinet carries NO COOKIES — hence `credentials: 'omit'`, stated rather
 *   than left to the default, because the default is `same-origin` and a future same-origin
 *   deployment would silently start sending them.
 * - The token is fetched per request from a callback rather than captured once. A Firebase ID
 *   token expires in an hour; a captured one turns into a 401 storm at minute sixty-one.
 * - What a 401 MEANS is not decided here. This app's five screen clients share one rule - a bare
 *   401 earns one forced refresh and one retry, a refused credential ends the session - in
 *   `features/auth/session/send-with-session.ts`, and this file hands it its `fetch`. The writes
 *   (submit, accept, the claims, the PATCH) are replayed too: a 401 was refused before any act.
 */
import type {
  Application,
  ApplicationSubmitRequest,
  AuthorSession,
  CategoryList,
  Credential,
  CredentialList,
  CredentialUploadClaim,
  OwnProfile,
  OwnProfileUpdate,
  SlugAvailability,
  StoredImage,
  TermsAcceptanceRequest,
  TermsState,
  UploadClaim,
} from '../contract';
import { i18n } from '../../../i18n';
import { sendWithSession } from '../../auth/session/send-with-session';
import { ApiError, TransportError, readProblem, retryAfterOf } from './problem';
import type { ApplicationApi, CallOptions } from './types';

export interface HttpApplicationApiOptions {
  /** Absolute, cross-origin. `web/authors/src/config.ts` already validates and de-slashes it. */
  readonly baseUrl: string;
  /**
   * Returns a CURRENT Firebase ID token, refreshing it if it is close to expiry. Returning
   * `undefined` sends the request unauthenticated, which the API answers 401 — that is the honest
   * outcome when the session has gone, and better than throwing a different shape from here.
   * `forceRefresh` is passed at most once per request, by the shared 401 rule.
   */
  readonly getIdToken: (forceRefresh?: boolean) => Promise<string | undefined>;
}

type Method = 'GET' | 'POST' | 'PATCH';

export function createHttpApplicationApi(options: HttpApplicationApiOptions): ApplicationApi {
  // This feature says "no token" with `undefined`; the shared 401 rule says it with `null`.
  const tokenSource = async (forceRefresh?: boolean): Promise<string | null> =>
    (await options.getIdToken(forceRefresh)) ?? null;

  async function request<T>(
    method: Method,
    path: string,
    body: unknown,
    call: CallOptions | undefined,
    query?: Readonly<Record<string, string | undefined>>,
  ): Promise<T> {
    const url = new URL(`${options.baseUrl}${path}`);
    if (query) {
      for (const [key, value] of Object.entries(query)) {
        if (value !== undefined) url.searchParams.set(key, value);
      }
    }

    const response = await sendWithSession(tokenSource, async (token) => {
      const headers: Record<string, string> = { Accept: 'application/json' };
      if (token !== null) headers.Authorization = `Bearer ${token}`;
      if (body !== undefined) headers['Content-Type'] = 'application/json';
      try {
        return await fetch(url, {
          method,
          headers,
          credentials: 'omit',
          ...(body === undefined ? {} : { body: JSON.stringify(body) }),
          ...(call?.signal ? { signal: call.signal } : {}),
        });
      } catch (cause) {
        // An aborted request is the caller's own doing and must not be dressed up as an outage.
        if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
        throw new TransportError(`${method} ${path} did not reach the API.`, cause);
      }
    });

    if (!response.ok) {
      const problem = await readProblem(response);
      const message = `${method} ${path} answered ${response.status}.`;
      // A 429's `Retry-After` rides on the error, for `humanMessage` to say how long to wait. The
      // reader is the one the cabinet's three clients use (`retryAfterOf`,
      // ../../cabinet/api/problem.ts), re-exported by ./problem.ts.
      throw new ApiError(response.status, problem, message, retryAfterOf(response.headers));
    }

    // 204 has no body. None of these sixteen operations returns one today, but a `.json()` on an
    // empty body throws a SyntaxError that reads like a server fault, so guard rather than hope.
    if (response.status === 204) return undefined as T;
    return (await response.json()) as T;
  }

  return {
    getAuthorSession: (call) => request<AuthorSession>('GET', '/v1/me', undefined, call),

    getApplication: (call) => request<Application>('GET', '/v1/application', undefined, call),

    submitApplication: (body: ApplicationSubmitRequest, call) =>
      request<Application>('POST', '/v1/application/submit', body, call),

    resubmitApplication: (body: ApplicationSubmitRequest, call) =>
      request<Application>('POST', '/v1/application/resubmit', body, call),

    getCurrentTerms: (call) => request<TermsState>('GET', '/v1/terms/current', undefined, call),

    acceptTerms: (body: TermsAcceptanceRequest, call) =>
      request<TermsState>('POST', '/v1/terms/acceptances', body, call),

    getOwnProfile: (call) => request<OwnProfile>('GET', '/v1/profile', undefined, call),

    updateOwnProfile: (body: OwnProfileUpdate, call) =>
      request<OwnProfile>('PATCH', '/v1/profile', body, call),

    // Omitting the parameter is meaningful: it asks for a SUGGESTION rather than a check.
    checkSlugAvailability: (slug: string | undefined, call) =>
      request<SlugAvailability>('GET', '/v1/profile/slug/availability', undefined, call, { slug }),

    claimAvatarUpload: (body: UploadClaim, call) =>
      request<StoredImage>('POST', '/v1/profile/avatar', body, call),

    listOwnCredentials: (call) =>
      request<CredentialList>('GET', '/v1/profile/credentials', undefined, call),

    claimCredentialUpload: (body: CredentialUploadClaim, call) =>
      request<Credential>('POST', '/v1/profile/credentials', body, call),

    // The names come in the cabinet's language of the moment (design 29, W5); the screen keys its
    // read on that language, so a change asks again.
    listEditorCategories: (call) =>
      request<CategoryList>('GET', '/v1/categories', undefined, call, { lang: i18n.language }),

    // Nothing typed asks for MoonX's own list too (`groups`), which is what the profile wants.
    suggestTags: (query, call) =>
      request<Awaited<ReturnType<ApplicationApi['suggestTags']>>>(
        'GET',
        '/v1/tags/suggestions',
        undefined,
        call,
        {
          q: query?.q,
          limit: query?.limit === undefined ? undefined : String(query.limit),
        },
      ),

    // Design 25: the documents and their acceptance, read and written as one list.
    listAgreements: (call) => request<AgreementList>('GET', '/v1/agreements', undefined, call),

    acceptAgreements: (body: AgreementAcceptRequest, call) =>
      request<AgreementList>('POST', '/v1/agreements/acceptances', body, call),
  };
}

/*
 * Design 25's two types are imported at the foot, not the head: lines of this file are cited by
 * number (src/modules/authors/services/credential.service.ts, features/auth/session/access-token.ts),
 * so nothing is added above them. Imports are hoisted, so where they are written changes nothing.
 */
import type { AgreementAcceptRequest, AgreementList } from '../contract';
